import { useCallback, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { isEqual } from 'lodash'
import {
  AutoSetupResult,
  commitAutoSetup,
  getTeamEvent,
  previewAutoSetup,
  UpdateEventActivityPayload,
} from 'app/frontend/base/api/event'
import { awaitPendingJobs, displayErrorToast } from 'app/frontend/base/helpers'
import { toast } from 'sonner'
import { useTeam } from 'app/frontend/base/config/context/team'
import AutoSetupSettings from 'app/frontend/types/custom/AutoSetupSettings'
import { useTranslation } from 'react-i18next'
import {
  applyAutoSetupResultToEvent,
  buildAutoSetupResultFromEvent,
} from '../components/autoSetupApply'
import type { EventPlanOverridesByPlanId } from '../components/eventActivityPlanOverrides'
import { diffPlanOverrides } from '../components/eventActivityPlanOverridesPatch'

type Args = {
  eventId?: number
  onRequestConfig: () => void
  onGenerateStart?: () => void
}

const activityOrderIds = (event: wowaudit.Event) =>
  event.activities
    .slice()
    .sort((left, right) => left.order - right.order)
    .map((activity) => activity.id)

const activityUpdatesFromDiff = (
  original: wowaudit.EventActivity,
  preview: wowaudit.EventActivity,
): UpdateEventActivityPayload | null => {
  const updates: UpdateEventActivityPayload = {}
  const planDiff = diffPlanOverrides(
    (original.planOverrides as EventPlanOverridesByPlanId) ?? {},
    (preview.planOverrides as EventPlanOverridesByPlanId) ?? {},
  )
  if (planDiff.kind === 'patch') updates.planOverridesPatch = planDiff.patch
  if (planDiff.kind === 'replace') updates.planOverrides = preview.planOverrides
  if (preview.active !== original.active) updates.active = preview.active
  if (preview.notes !== original.notes) updates.notes = preview.notes
  if (!isEqual(preview.linkedPlans, original.linkedPlans)) {
    updates.linkedPlans = preview.linkedPlans
  }
  return Object.keys(updates).length > 0 ? updates : null
}

const useAutoSetupPreview = ({
  eventId,
  onRequestConfig,
  onGenerateStart,
}: Args) => {
  const { t } = useTranslation()
  const { team } = useTeam()
  const queryClient = useQueryClient()
  const [isActive, setIsActive] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  const [isDiscarding, setIsDiscarding] = useState(false)
  const [isCommitting, setIsCommitting] = useState(false)
  const [lastActivityIds, setLastActivityIds] = useState<number[]>()
  const baseEventRef = useRef<wowaudit.Event>()
  const settingsRef = useRef<AutoSetupSettings>()
  const activityIdsRef = useRef<number[]>()
  const generationIdRef = useRef(0)
  const isActiveRef = useRef(false)
  const onRequestConfigRef = useRef(onRequestConfig)
  const onGenerateStartRef = useRef(onGenerateStart)
  onRequestConfigRef.current = onRequestConfig
  onGenerateStartRef.current = onGenerateStart
  isActiveRef.current = isActive

  const teamEventQueryKey =
    eventId != null
      ? (['teamEvent', { teamId: team.id, eventId }] as const)
      : null

  const freezeEventQuery = useCallback(async () => {
    if (!teamEventQueryKey) return
    await queryClient.cancelQueries({ queryKey: teamEventQueryKey })
  }, [queryClient, teamEventQueryKey])

  const writePreviewEvent = useCallback(
    (event: wowaudit.Event) => {
      if (!teamEventQueryKey) return
      queryClient.setQueryData(teamEventQueryKey, event)
    },
    [queryClient, teamEventQueryKey],
  )

  const patchPreviewEvent = useCallback(
    (apply: (event: wowaudit.Event) => wowaudit.Event) => {
      if (!teamEventQueryKey) return
      const current =
        queryClient.getQueryData<wowaudit.Event>(teamEventQueryKey)
      if (!current) return
      queryClient.setQueryData(teamEventQueryKey, apply(current))
    },
    [queryClient, teamEventQueryKey],
  )

  const loadFreshEvent = useCallback(async () => {
    if (!eventId || !teamEventQueryKey) return
    const fresh = await getTeamEvent(team.id, eventId)
    queryClient.setQueryData(teamEventQueryKey, fresh)
  }, [eventId, queryClient, team.id, teamEventQueryKey])

  const exitPreview = useCallback(() => {
    generationIdRef.current += 1
    setIsActive(false)
    setIsGenerating(false)
    setIsDiscarding(false)
    setIsCommitting(false)
    baseEventRef.current = undefined
  }, [])

  const runGenerate = useCallback(
    async (settings: AutoSetupSettings, activityIds: number[]) => {
      if (!eventId) return
      const generationId = ++generationIdRef.current
      settingsRef.current = settings
      activityIdsRef.current = activityIds
      setLastActivityIds(activityIds)
      onGenerateStartRef.current?.()
      setIsGenerating(true)
      await freezeEventQuery()

      try {
        const { job_id } = await previewAutoSetup(team.id, eventId, {
          activityIds,
          settings,
        })
        const [job] = await awaitPendingJobs<AutoSetupResult>([job_id])
        if (generationId !== generationIdRef.current) return

        if (job.error || !job.content) {
          displayErrorToast(
            new Error(job.error ?? 'Failed to generate setup'),
            t,
          )
          setIsGenerating(false)
          if (!isActiveRef.current) onRequestConfigRef.current()
          return
        }

        const baseEvent = await getTeamEvent(team.id, eventId)
        if (generationId !== generationIdRef.current) return

        baseEventRef.current = baseEvent
        writePreviewEvent(
          applyAutoSetupResultToEvent(
            baseEvent,
            job.content as AutoSetupResult,
          ),
        )
        setIsActive(true)
      } catch (error) {
        if (generationId !== generationIdRef.current) return
        displayErrorToast(error as Error, t)
        if (!isActiveRef.current) onRequestConfigRef.current()
      } finally {
        if (generationId === generationIdRef.current) setIsGenerating(false)
      }
    },
    [eventId, freezeEventQuery, t, team.id, writePreviewEvent],
  )

  const generate = useCallback(
    (settings: AutoSetupSettings, activityIds: number[]) => {
      void runGenerate(settings, activityIds)
    },
    [runGenerate],
  )

  const regenerate = useCallback(() => {
    const settings = settingsRef.current
    const activityIds = activityIdsRef.current
    if (!settings || !activityIds?.length) return
    void runGenerate(settings, activityIds)
  }, [runGenerate])

  const discardPreview = useCallback(async () => {
    generationIdRef.current += 1
    setIsDiscarding(true)
    await loadFreshEvent()
    exitPreview()
  }, [exitPreview, loadFreshEvent])

  const discard = useCallback(() => {
    void discardPreview().catch((error) => {
      setIsDiscarding(false)
      displayErrorToast(error as Error, t)
    })
  }, [discardPreview, t])

  const discardAndConfigure = useCallback(() => {
    onRequestConfigRef.current()
  }, [])

  const commit = useCallback(async () => {
    if (!eventId || !teamEventQueryKey) return
    const previewEvent =
      queryClient.getQueryData<wowaudit.Event>(teamEventQueryKey)
    const baseEvent = baseEventRef.current
    if (!previewEvent || !baseEvent) return

    setIsCommitting(true)
    generationIdRef.current += 1
    try {
      const originalById = new Map(
        baseEvent.activities.map((activity) => [activity.id, activity]),
      )
      const activityUpdates = previewEvent.activities.flatMap((activity) => {
        const original = originalById.get(activity.id)
        if (!original) return []
        const updates = activityUpdatesFromDiff(original, activity)
        return updates ? [{ activityId: activity.id, ...updates }] : []
      })
      const previewOrder = activityOrderIds(previewEvent)
      const baseOrder = activityOrderIds(baseEvent)

      await commitAutoSetup(
        team.id,
        eventId,
        buildAutoSetupResultFromEvent(previewEvent),
        {
          activityUpdates,
          activityOrder: isEqual(previewOrder, baseOrder)
            ? undefined
            : previewOrder,
        },
      )

      await loadFreshEvent()
      exitPreview()
      toast.success('Setup applied successfully')
    } catch (error) {
      writePreviewEvent(previewEvent)
      displayErrorToast(error as Error, t)
    } finally {
      setIsCommitting(false)
    }
  }, [
    eventId,
    exitPreview,
    loadFreshEvent,
    queryClient,
    t,
    team.id,
    teamEventQueryKey,
    writePreviewEvent,
  ])

  return {
    isActive,
    isGenerating,
    isDiscarding,
    isCommitting,
    isFrozen: isActive || isGenerating || isDiscarding,
    lastActivityIds,
    generate,
    regenerate,
    commit,
    discard,
    discardAndConfigure,
    patchPreviewEvent,
  }
}

export default useAutoSetupPreview
